import { BaseIdentifiers, UserIdentifier } from '../common/identifiers';

export type Notification = BaseIdentifiers &
  UserIdentifier & {
    platforms: NotificationPlatform[];
    message?: string;
    type: NotificationName;
    seen: boolean;
    i18n?: { message: i18nMessage };
  };

export enum NotificationPlatform {
  APP = 'app',
  EMAIL = 'email',
}

export enum NotificationName {
  Termination = 'termination',
  EvaluationAboutToExpire = 'about-to-expire',
  DemoAboutToExpire = 'demo-is-about-to-expired',
  ReachedExpiration = 'reached-expiration',
  ReachedDailyPause = 'daily-pause-reached',
  ReachedDailyLoss = 'daily-loss-reached',
  AfterPurchase = 'after-purchasing',
  ResetPassword = 'reset-password',
  Welcome = 'welcome',
  UserVerificationRequired = 'user-verification-required',
  UserVerificationCompleted = 'user-verification-completed',
  UserVerificationFailed = 'user-verification-failed',
  EmailConfirmation = 'confirmation-email',
  QuickPurchaseCode = '26-test-quick-purchase-code',
  NewAccount = 'new-account',
  AddOnApplied = 'add-on-applied',
  ReachedTargetEvaluationToEvaluation = 'eval-to-eval-passed',
  ReachedTargetToFunded = 'email-15-reached-target-account-passed',
  ResetDailyPause = 'email-13-account-renewed-after-daily-pause',
  FundedAccountReady = 'email-17-funded-account-is-ready',
  FundedAccountReadyInterview = 'funded-account-ready-2-interview',
  RequestPayout = 'email-payout-request-confirmation',
  GotPaid = 'email-16-you-got-paid',
  InterviewInvitation = '26-interview',
  ReachedInactivityDays = 'reached-inactivity-days',
  WarnReachingInactivityDays = 'warn-reaching-inactivity-days',
  AdminAccountPayout = 'admin-account-payout',
  AdminAffiliatePayout = 'admin-affiliate-payout',
  AdminPayoutRequest = 'admin-payout-request',
  NewTradeTalkPublished = 'new-trade-talk-published',
  TradeTalkCoolTime = 'trade-talk-cool-time',
  WithdrawalRequest = 'email-18-withdrawal-request',
  WithdrawalAffiliateRequest = 'affiliate-withdraw-verification',
  Contest = 'contest',
  ArticleRejected = 'article-rejected',
  ArticleUnderReview = 'article-in-review',
  ArticleApproved = 'article-approved',
  ContestRegistration = 'contest-registration',
  ContestPrize = 'contest-prize',
  ContestStarted = 'contest-started',
  ContestLastDay = 'contest-last-day',
  ContestEnded = 'contest-ended',
  MaxLossTargetReached = 'max-loss-target-reached',
  PreFundedProcess = 'email-10---pre-funded-process-1',
  TalkCommentReceived = 'talk-comment-received',
  TalkLikeReceived = 'talk-like-received',
  TalkEditorPickReceived = 'talk-editor-pick-received',
  TradingEndWarning = 'futures-hold-positions-overnight-reminder',
  WeekendWarning = 'over-the-weekend-futures',
  CreditExpirationWithin2Days = 'credit-expiration-within-2-days',
  CreditExpirationWithin7Days = 'credit-expiration-within-7-days',
  HubCreditReceived = 'hub-credit-received',
  WithdrawalSocialMediaPromotionCoupon = '26-first-time-approval',
  WithdrawalSocialMediaPromotionCredits = '26-second-time-approval',
}

export type NotificationData = {
  message: string;
  id: string;
  type?: NotificationName;
  /** Interpolation payload (nova notifications); legacy may expose via i18n.message.values only. */
  values?: Record<string, string | number>;
  i18n?: {
    message: i18nMessage;
  };
  createdAt?: string;
  icon?: string;
  realTime?: boolean;
  seen?: boolean;
  talkId?: string;
  commentId?: string;
  replacesNotificationId?: string;
  commenters?: Array<{ userId: string; userName: string }>;
  likers?: Array<{ userId: string; userName: string }>;
};

export interface i18nMessage {
  key: string;
  values?: Record<string, string | number>;
  defaultValue?: string;
}

export enum NotificationKeys {
  UserVerificationRequired = 'notificationCenter:messages.kyc_required',
  UserVerificationCompleted = 'notificationCenter:messages.kyc_completed',
  NewAccount = 'notificationCenter:messages.new_account',
  MaxLossTargetReached = 'notificationCenter:messages.max_loss_target_reached',
  Renew = 'notificationCenter:messages.renew',
  ArticleApproved = 'notificationCenter:messages.article_approved',
  ArticleUnderReview = 'notificationCenter:messages.article_under_review',
  ArticleRejected = 'notificationCenter:messages.article_rejected',
  NewTradeTalkPublished = 'notificationCenter:messages.new_trade_talk_published',
  TradeTalkCoolTime = 'notificationCenter:messages.trade_talk_cool_time',
  ContestPrizeClaimed = 'notificationCenter:messages.contest_prize_claimed',
  // Talk notifications
  TalkCommentSingle = 'notificationCenter:messages.talk_comment_single',
  TalkCommentMultiple = 'notificationCenter:messages.talk_comment_multiple',
  TalkLikeSingle = 'notificationCenter:messages.talk_like_single',
  TalkLikeMultiple = 'notificationCenter:messages.talk_like_multiple',
  TalkEditorPick = 'notificationCenter:messages.talk_editor_pick',
}

export enum FuturesSupportedTemplateNames {
  AccountPassedFunded = '26-account-passed-funded',
  AfterPurchaseNewAccount = '26-after-purchase-new-account',
  ConfirmYourEmail = '26-confirm-your-email',
  EvalToEvalPassed = '26-eval-to-eval-passed',
  EvalToFunded = '26-eval-to-funded',
  FundedAccountActivated = '26-funded-account-activated',
  HubRegistrationWelcomeToThe5ers = '26-hub-registration-welcome-to-the5ers',
  InactivityReminder = '26-inactivity-reminder',
  Kyc = '26-kyc',
  MaxLossReached = '26-max-loss-reached',
  ProfitRequested = '26-profit-requested',
  ReSubscribe = '26-re-subscribe',
  WithdrawalVerification = '26-withdrawal-verification',
  YouGotPaid = '26-you-got-paid',
}
export const getFuturesSupportedTemplateNames = (templateName: string) => {
  const futuresSupportedTemplateNames = Object.values(
    FuturesSupportedTemplateNames,
  );

  if (
    futuresSupportedTemplateNames.includes(
      templateName as FuturesSupportedTemplateNames,
    )
  ) {
    return `${templateName}-futures`;
  }

  return templateName;
};
